class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.rebindAll();

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer') return;
      setTimeout(() => this.rebindAll(), 250);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  get isOpen() {
    return this.classList.contains('active');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (el && parsedState.sections[section.id]) {
        el.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    const hasItems = !!this.querySelector('.cart-item');
    this.classList.toggle('is-empty', !hasItems);

    setTimeout(() => {
      if (!this.isOpen) this.open();
      this.rebindAll();
    }, 50);
  }

  rebindAll() {
    this.bindUpsellAddButtons();
    this.bindMinusAsRemove();
    if (window.__initUpsellSlider) window.__initUpsellSlider();
  }

  /* ── Minus = Remove when qty is 1 ── */
  bindMinusAsRemove() {
    const drawer = this;
    this.querySelectorAll('quantity-input.cart-quantity').forEach((qi) => {
      const minusBtn = qi.querySelector('button[name="minus"]');
      const input = qi.querySelector('input');
      if (!minusBtn || !input || minusBtn.dataset.oioiBound) return;
      minusBtn.dataset.oioiBound = 'true';

      minusBtn.addEventListener(
        'click',
        (e) => {
          const val = parseInt(input.value);
          const min = parseInt(input.min) || 1;
          if (val <= min) {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();

            const cartItems =
              qi.closest('cart-drawer-items') || qi.closest('cart-items');
            const index = input.dataset.index;
            if (cartItems && index) {
              cartItems.updateQuantity(index, 0);
            }
          }
        },
        true
      );
    });
  }

  /* ── Upsell AJAX add ── */
  bindUpsellAddButtons() {
    this.querySelectorAll(
      '.cart-drawer__upsell-item__add[data-variant-id]'
    ).forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleUpsellAdd(btn);
      });
    });
  }

  handleUpsellAdd(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '...';

    const sectionIds = this.getSectionsToRender().map((s) => s.id);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status) {
          btn.textContent = 'Hata';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
          return;
        }

        if (response.sections) {
          this.renderContents(response);
        } else {
          this.refreshDrawer();
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' });
      })
      .catch(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  refreshDrawer() {
    const sectionIds = this.getSectionsToRender()
      .map((s) => s.id)
      .join(',');
    fetch(`${window.location.pathname}?sections=${sectionIds}`)
      .then((res) => res.json())
      .then((sections) => {
        this.getSectionsToRender().forEach((section) => {
          const el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (el && sections[section.id]) {
            el.innerHTML = this.getSectionInnerHTML(
              sections[section.id],
              section.selector
            );
          }
        });
        const hasItems = !!this.querySelector('.cart-item');
        this.classList.toggle('is-empty', !hasItems);
        this.rebindAll();
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer .drawer__inner' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'cart-drawer') return;
      this.onCartUpdate();
    });
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshInner = doc.querySelector('#CartDrawer .drawer__inner');
        const target = document.querySelector('#CartDrawer .drawer__inner');
        if (freshInner && target) {
          target.innerHTML = freshInner.innerHTML;
        }
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const hasItems = !!cartDrawer.querySelector('.cart-item');
          cartDrawer.classList.toggle('is-empty', !hasItems);
          setTimeout(() => cartDrawer.rebindAll(), 50);
        }
      })
      .catch((e) => console.error(e));
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
